import { forwardRef, useContext, useImperativeHandle, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { ISearchIndividualInput, ISearchIndividualResponse, ISearchIndividualResult } from "../../../types/individual";
import { GET_POSITION_DETAIL } from "../../../graphql/position-gql";
import { GET_INDIVIDUAL, GET_INDIVIDUAL_DETAILS, SEARCH_INDIVIDUAL } from "../../../graphql/individual-gql";
import SearchIndividualForm, { SearchIndividualFormRef } from "./search-individual-form";
import SearchIndividualResult, { searchIndividualSelectedIndividual, searchIndividualSelectedTitle } from "./search-individual-result";
import { notifySuccess, notifyWarning } from "../../../services/notification";
import { OrgReducerContext, errorSearch, messageAfterSearchResult } from "../../org/org-context";
import { IOrgResponse } from "../../../types/org";
import { GET_ORG_DETAILS } from "../../../graphql/org-gql";
import { Spinner } from "reactstrap";
import SpinnerOverlay from "../../common/spinner/spinner";


export type SearchIndividualRef = {
  unselect: () => void;
};

const SearchIndividual = forwardRef<SearchIndividualRef>((prop,ref) => {
  const [searchResults, setSearchResults] = useState<ISearchIndividualResult[] | undefined>();
  //const [selected, setSelected] = useState<ISearchIndividualResult | undefined>();
  const [inputChanged,setInutChanged] = useState(true)
  const [getPositionDetail,{loading:positionLoading}]= useLazyQuery(GET_POSITION_DETAIL)
  const [getIndividualDetails,{loading:individualDetailsLoading}]= useLazyQuery(GET_INDIVIDUAL_DETAILS)
  const [getIndividual,{loading:individualLoading}]= useLazyQuery(GET_INDIVIDUAL)
  const [searchIndividual, { loading:searchIndividualLoading, error: searchIndividualError,data:searchIndividualData }] = useLazyQuery<ISearchIndividualResponse>(SEARCH_INDIVIDUAL);
  const [getOrgDetail,{loading:orgLoading}] = useLazyQuery<IOrgResponse>(GET_ORG_DETAILS)
  const searchIndividualFormRef = useRef<SearchIndividualFormRef | null>(null);
  const [state, dispatch] = useContext(OrgReducerContext);
  const [isInputError, setIsInputError] = useState(false);
  const resetState = () => dispatchEvent

  const unselect=()=>{
    resetState();
  }

  useImperativeHandle(ref, () => ({
    unselect
  }));

  const onSearch=(input:ISearchIndividualInput,isValid: boolean)=>{
    //console.log("isValid",isValid)
    dispatch({type:'reset'})
    setSearchResults([]);
    if(!isValid){
      dispatch({type:'set-individual-search-error',payload:errorSearch})
      dispatch({type:'set-individual-search-message'})
      setIsInputError(true);
    }else
      searchIndividual(
        { variables: { input },fetchPolicy: 'no-cache',
        onCompleted: (data) => {data && notifySuccess()}
      }).then((response:any )=> {
              response.data?.searchIndividual?.message && notifyWarning(response.data.searchIndividual?.message)
              //console.log("response.data?.searchIndividual:",response.data?.searchIndividual)
              updateSearchResults(response.data?.searchIndividual.individuals)
              setTimeout(() => { dispatch({type:'set-individual-search-message',payload:messageAfterSearchResult}); }, 100);
              dispatch({type:'set-individual-search-error'})
          });

  }

  const onChange = () => {
    setInutChanged(true)
  }

  const updateSearchResults = (results: ISearchIndividualResult[] | undefined) => {
    setInutChanged(false)
    if(results){
      //console.log("results:",results)
      if(results.length>700)
        results = results.slice(0,700);
      setSearchResults(results);
    }
  }

  const onTitleSelect = async (item: ISearchIndividualResult) => {
    try {
      dispatch({type: 'reset'});
      dispatch({type: 'set-last-selected-item', payload: searchIndividualSelectedTitle(item) });
      let positionDetailResponse=undefined;
      if(item.positionId){
        positionDetailResponse = await getPositionDetail({ variables: { positionId: item.positionId } });
        if (positionDetailResponse.data?.position) {
          dispatch({
            type: 'set-individual-search-title',
            payload: positionDetailResponse.data.position
          });
        }
        if (positionDetailResponse.data?.position?.orgId) {
          getOrgDetail({ variables: { id:positionDetailResponse.data.position.orgId } }).then((response) => {
            if(response?.data?.org){
               dispatch({
                type:'set-individual-search-org',
                payload: response.data.org
              });
            }else{
              dispatch({type:'set-individual-search-org'});
            }
          })
        }
      }
    } catch (error:any) {
      console.error('Error:', error.message);
    }
  }

  const onIndividualNameSelect = async (item: ISearchIndividualResult) => {
    try {
        dispatch({type: 'reset'});
        dispatch({type: 'set-last-selected-item', payload: searchIndividualSelectedIndividual(item) });
        let individualDetailResponse =  undefined;
        if(item.assignmentId && item.assignment !='Position Based'){
          individualDetailResponse = await getIndividualDetails({ variables: { positionAssignmentId: item.assignmentId,individualId:item.individualId} });
        }
        else if(item.individualId)
          individualDetailResponse = await getIndividual({variables:{individualId:item.individualId}})
        let positionDetailResponse=undefined;
        if(item.positionId)
          positionDetailResponse = await getPositionDetail({ variables: { positionId: item.positionId } });
        let orgDetailResponse = undefined;
        if(positionDetailResponse?.data?.position)
          orgDetailResponse = await getOrgDetail({ variables: { id: positionDetailResponse.data.position.orgId } });
        if(individualDetailResponse?.data?.individualAssignment){
          //console.log("individualDetailResponse?.data",individualDetailResponse?.data)
          dispatch({
            type: 'set-individual-search-individual',
            payload: individualDetailResponse.data.individualAssignment
          });
          dispatch({type:'set-individual-search-message'})
        }else if(individualDetailResponse?.data?.individual){
          const result = individualDetailResponse.data.individual;
          //console.log("result:",result)
          const individual = {...result,honorific:result.honorificTitle,honorificTitle:result.honorificTitle?.label}
          //console.log("individual:",individual)
          dispatch({
            type: 'set-individual-search-individual',
            payload: individual
          });
        //   dispatch({type:'set-individual-search-message'})
        }
        if(positionDetailResponse?.data?.position){
          dispatch({
            type: 'set-individual-search-position',
            payload: positionDetailResponse.data.position
          });
        }
        if(orgDetailResponse?.data?.org){
          dispatch({
            type: 'set-individual-search-org',
            payload: orgDetailResponse.data.org
          });
        }else{
          dispatch({type:'set-individual-search-org'});
        }
      } catch (error:any) {
        console.error('Error:', error.message);
      }
  }

  return (
    <section id="search_individual">
     {(searchIndividualLoading || positionLoading || individualDetailsLoading || individualLoading) && <SpinnerOverlay/>}
      <SearchIndividualForm onSearch={onSearch} onChange={onChange} loading={searchIndividualLoading} error={searchIndividualError} ref={searchIndividualFormRef}></SearchIndividualForm>
      <br/>
      <SearchIndividualResult data={searchResults} onTitleSelect={onTitleSelect} onIndividualNameSelect={onIndividualNameSelect} inputChanged={inputChanged} selected={state?.lastSelectedItem} isInputError={isInputError}/>
    </section>
  )
});

export default SearchIndividual;
